import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { ArrowRightOnRectangleIcon, ArrowLeftOnRectangleIcon, MapPinIcon, UsersIcon, ClockIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import PopoverComponentCustom from "./popover_component";
import Footer from "./footer";
import WorkingAreaPresenceContextBar from "./working_area_presence_context_bar";
import PrettyPagination from "./pretty_pagination";
import { AuthContext } from "../contexts/auth";
import { getWorkingAreaList, getWorkingAreaPresences, getWorkingAreaPresenceDetail } from "../services/api_working_area";
import { estraiMessaggiErrore } from "../services/utils";
import LocationMap from "./location_map";

const DETAIL_PAGE_SIZE = 100;

function SummaryCard({ title, value, description, icon: Icon }) {
    return (
        <div className="rounded-xl border border-indigo-200 bg-gray-50 p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
                    <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
                    <p className="mt-1 text-xs text-gray-500">{description}</p>
                </div>

                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-indigo-100 bg-indigo-50 text-indigo-600">
                    <Icon className="h-6 w-6" />
                </div>
            </div>
        </div>
    );
}


function WorkingAreaPresenceRow({ token, area, refreshKey }) {
    const [open, setOpen] = useState(false);
    const [details, setDetails] = useState([]);
    const [detailsLoaded, setDetailsLoaded] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [detailsError, setDetailsError] = useState("");
    const [detailPage, setDetailPage] = useState(1);
    const [detailTotalCount, setDetailTotalCount] = useState(0);
    const [detailStatus, setDetailStatus] = useState("all");
    const [selectedLocation, setSelectedLocation] = useState(null);

    const controller = useRef(null);

    const detailTotalPages = Math.ceil(detailTotalCount / DETAIL_PAGE_SIZE);

    const fetchDetails = async () => {
        if (!token) return;

        setLoadingDetails(true);
        setDetailsError("");

        controller.current?.abort();
        controller.current = new AbortController();

        const signal = controller.current.signal;

        try {
            const response = await getWorkingAreaPresenceDetail(token, area.id, { page: detailPage, page_size: DETAIL_PAGE_SIZE, status: detailStatus, }, signal, 'shifts');

            setDetails(response?.results || []);
            setDetailTotalCount(response?.count || 0);
            setDetailsLoaded(true);
        } catch (error) {
            if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
                console.error("Errore caricamento dettaglio presenze area:", error);
                setDetailsError(estraiMessaggiErrore(error) || "Errore nel caricamento del dettaglio");
            }
        } finally {
            if (!signal.aborted) setLoadingDetails(false);
        }
    };

    const handleToggle = () => {
        const nextOpen = !open;

        if (!nextOpen) {
            setOpen(false);
            setDetailStatus("all");
            setDetailPage(1);
            setDetails([]);
            setDetailsLoaded(false);
            setDetailTotalCount(0);

            return;
        }

        setOpen(true);
    };

    useEffect(() => {
        if (!open) return;

        fetchDetails();
    }, [open, detailPage, detailStatus]);

    const handleDetailStatusChange = (status) => {
        if (status === detailStatus) return;

        setDetailStatus(status);
        setDetailPage(1);
    };

    useEffect(() => {
        if (!open) return;

        if (detailPage !== 1) {
            setDetailPage(1);
            return;
        }

        fetchDetails();
    }, [refreshKey]);

    const openLocationModal = (type, latitude, longitude) => {
        if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) return;

        setSelectedLocation({ type, latitude: Number(latitude), longitude: Number(longitude) });
    };

    const closeLocationModal = () => {
        setSelectedLocation(null);
    };


    useEffect(() => {
        return () => {controller.current?.abort();};
    }, []);

    return (
        <Fragment>
            <tr onClick={handleToggle} className="cursor-pointer border-b border-gray-300/80 transition-colors hover:bg-indigo-50/40">
                <td className="w-12 px-4 py-4">
                    <button
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleToggle();
                        }}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-indigo-600"
                    >
                        {open ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                    </button>
                </td>

                <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                            <MapPinIcon className="h-5 w-5" />
                        </div>

                        <span className="text-gray-800">{area.name}</span>
                    </div>
                </td>

                <td className="px-4 py-4 text-right font-semibold text-gray-700">{area.total_checkins}</td>
                <td className="px-4 py-4 text-right font-semibold text-gray-700">{area.total_checkouts}</td>

                <td className="px-4 py-4 text-right">
                    <span className={`inline-flex min-w-[42px] justify-center rounded-full px-2.5 py-1 text-xs font-semibold ${area.presences_now > 0 ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-500"}`}>
                        {area.presences_now}
                    </span>
                </td>
            </tr>

            {open && (
                <tr>
                    <td colSpan={5} className="border-b border-indigo-100 bg-indigo-50/50 px-6 py-5">
                        {loadingDetails ? (
                            <div className="flex justify-center py-6">
                                <CircularProgress size={28} />
                            </div>
                        ) : detailsError ? (
                            <Alert severity="error">{detailsError}</Alert>
                        ) : (
                            <div>
                                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                    <p className="text-xs font-semibold tracking-wide text-indigo-700">
                                        DETTAGLIO -
                                        <span className="text-xs font-normal text-gray-500"> Timbrature registrate nell'area</span>
                                    </p>

                                    <div className="inline-flex w-fit rounded-lg border border-gray-200 bg-white">
                                        {[
                                            {value: "all", label: "Tutti"},
                                            {value: "in", label: "Presenti"},
                                            {value: "out", label: "Usciti"},
                                        ].map((option) => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => handleDetailStatusChange(option.value)}
                                                className={`border-l border-gray-300 px-3 py-1.5 text-xs font-semibold transition-colors ${
                                                    detailStatus === option.value
                                                        ? "bg-indigo-200 text-indigo-700"
                                                        : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                                }`}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                                    <table className="w-full border-collapse text-sm">
                                        <thead className="bg-gray-50">
                                            <tr className="border-b border-gray-200">
                                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Dipendente</th>
                                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-500">Ingresso</th>
                                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-500">Uscita</th>
                                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-500">Stato</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {details.length > 0 ? (
                                                details.map((detail) => {
                                                    const employeeName = [detail.last_name, detail.first_name].filter(Boolean).join(" ");
                                                    const employeeLabel = employeeName || detail.cf || "-";

                                                    return (
                                                        <tr key={detail.id} className="border-b border-gray-100 last:border-b-0">
                                                            <td className="px-4 py-3 font-medium text-gray-800">{employeeLabel}</td>
                                                            <td className="px-4 py-3">
                                                                <div className="flex items-center justify-center">
                                                                    <span className="min-w-[56px] whitespace-nowrap text-right">
                                                                        {detail.checkin ? (
                                                                            (() => {
                                                                                const checkin = new Date(detail.checkin);
                                                                                const today = new Date();

                                                                                return (
                                                                                    checkin.getFullYear() === today.getFullYear() &&
                                                                                    checkin.getMonth() === today.getMonth() &&
                                                                                    checkin.getDate() === today.getDate()
                                                                                )
                                                                                    ? checkin.toLocaleTimeString("it-IT", {
                                                                                        hour: "2-digit",
                                                                                        minute: "2-digit",
                                                                                    })
                                                                                    : `${checkin.toLocaleDateString("it-IT")} ${checkin.toLocaleTimeString("it-IT", {
                                                                                        hour: "2-digit",
                                                                                        minute: "2-digit",
                                                                                    })}`;
                                                                            })()
                                                                        ) : (
                                                                            "-"
                                                                        )}
                                                                    </span>

                                                                    <span className="ml-1 flex w-6 justify-center">
                                                                        {detail.checkin_lat != null &&
                                                                        detail.checkin_lng != null && (
                                                                            <button
                                                                                type="button"
                                                                                title="Visualizza posizione ingresso"
                                                                                onClick={(event) => {
                                                                                    event.stopPropagation();
                                                                                    openLocationModal("checkin", detail.checkin_lat, detail.checkin_lng);
                                                                                }}
                                                                                className="inline-flex h-6 w-6 items-center justify-center rounded-full text-indigo-600 hover:bg-indigo-100"
                                                                            >
                                                                                <MapPinIcon className="h-4 w-4" />
                                                                            </button>
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="flex items-center justify-center">
                                                                    <span className="w-14 text-right">
                                                                        {detail.checkout ? new Date(detail.checkout).toLocaleTimeString("it-IT", {hour: "2-digit", minute: "2-digit"}) : "-"}
                                                                    </span>

                                                                    <span className="ml-1 flex w-6 justify-center">
                                                                        {detail.checkout_lat != null && detail.checkout_lng != null && (
                                                                            <button
                                                                                type="button"
                                                                                title="Visualizza posizione uscita"
                                                                                onClick={(event) => {
                                                                                    event.stopPropagation();
                                                                                    openLocationModal("checkout", detail.checkout_lat, detail.checkout_lng);
                                                                                }}
                                                                                className="inline-flex h-6 w-6 items-center justify-center rounded-full text-indigo-600 hover:bg-indigo-100"
                                                                            >
                                                                                <MapPinIcon className="h-4 w-4" />
                                                                            </button>
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </td>

                                                            <td className="px-4 py-3 text-center">
                                                                {detail.checkout ? (
                                                                    <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">Uscito</span>
                                                                ) : (
                                                                    <span className="inline-flex rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-700">Presente</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500">Nessuna presenza registrata nell'area</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {detailTotalPages > 1 && (
                                    <PrettyPagination
                                        page={detailPage}
                                        totalPages={detailTotalPages}
                                        totalCount={detailTotalCount}
                                        onChange={setDetailPage}
                                        labelSingolare="timbratura"
                                        labelPlurale="timbrature"
                                        className="mt-3 rounded-lg border border-gray-200 bg-white px-4 py-3"
                                    />
                                )}
                            </div>
                        )}
                    </td>
                </tr>
            )}

            {selectedLocation && (
                <tr>
                    <td>
                        <div
                            className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/40 px-4 py-6 sm:py-10"
                            onClick={closeLocationModal}
                        >
                            <div
                                className="w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-xl"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 bg-indigo-100">
                                    <div>
                                        <p className="text-mf font-semibold uppercase tracking-wide text-indigo-800">
                                            {selectedLocation.type === "checkin" ? "Posizione ingresso" : "Posizione uscita"}
                                        </p>

                                        <p className="mt-1 text-xs text-gray-500">
                                            Posizione registrata al momento della timbratura
                                        </p>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={closeLocationModal}
                                        className="rounded-md px-3 py-1.5 text-sm font-semibold border border-gray-200 bg-white text-gray-700 transition hover:bg-gray-100 hover:text-gray-700"
                                    >
                                        CHIUDI
                                    </button>
                                </div>

                                <div className="h-[450px] w-full">
                                    <LocationMap locations={[{type: selectedLocation.type, latitude: selectedLocation.latitude, longitude: selectedLocation.longitude}]} />
                                </div>

                                <div className="border-t border-gray-200 bg-gray-50 px-5 py-3">
                                    <p className="text-xs text-gray-500">
                                        Latitudine: <span className="font-medium text-gray-700">{selectedLocation.latitude}</span>
                                        {" · "}
                                        Longitudine: <span className="font-medium text-gray-700">{selectedLocation.longitude}</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            )}

        </Fragment>
    );
}


export default function PresencesWorkingArea() {
    const { profile, token } = useContext(AuthContext);
    const isProfileComplete = profile?.has_complete_profile;

    const [workingAreaOptions, setWorkingAreaOptions] = useState([]);
    const [selectedWorkingArea, setSelectedWorkingArea] = useState(null);
    const [workingAreas, setWorkingAreas] = useState([]);

    const [totalCount, setTotalCount] = useState(0);
    const [totalCheckins, setTotalCheckins] = useState(0);
    const [totalCheckouts, setTotalCheckouts] = useState(0);
    const [presencesNow, setPresencesNow] = useState(0);
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [detailsRefreshKey, setDetailsRefreshKey] = useState(0);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const controller = useRef(null);

    const totalPages = Math.ceil(totalCount / pageSize);


    // Aree disponibili nella select
    useEffect(() => {
        const fetchWorkingAreaOptions = async () => {
            if (!token) return;

            try {
                const response = await getWorkingAreaList(token, { status: "active", page_size: 100 }, 'shifts');
                setWorkingAreaOptions(response?.results || []);
            } catch (error) {
                console.error("Errore caricamento aree di lavoro:", error);
            }
        };

        fetchWorkingAreaOptions();
    }, [token]);


    const fetchPresences = async () => {
        if (!token) return;

        setLoading(true);
        setError("");

        controller.current?.abort();
        controller.current = new AbortController();

        const signal = controller.current.signal;

        try {
            const response = await getWorkingAreaPresences(
                token,
                { working_area: selectedWorkingArea?.id || undefined, page, page_size: pageSize },
                signal,
                'shifts'
            );

            setWorkingAreas(response?.results || []);
            setTotalCount(response?.count || 0);
            setTotalCheckins(response?.total_checkins || 0);
            setTotalCheckouts(response?.total_checkouts || 0);
            setPresencesNow(response?.presences_now || 0);
            setLastUpdated(new Date());
        } catch (error) {
            if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
                console.error("Errore caricamento presenze aree:", error);
                setError(estraiMessaggiErrore(error) || "Errore nel caricamento delle presenze per area di lavoro");
            }
        } finally {
            if (!signal.aborted) setLoading(false);
        }
    };

    // Situazione presenze
    useEffect(() => {
        fetchPresences();

        return () => {controller.current?.abort();};
    }, [token, selectedWorkingArea, page, pageSize]);


    const handleRefresh = async () => {
        await fetchPresences();
        setDetailsRefreshKey((value) => value + 1);
    };


    const handleWorkingAreaChange = (workingArea) => {
        setSelectedWorkingArea(workingArea);
        setPage(1);
    };


    const handlePageSizeChange = (value) => {
        setPageSize(Number(value));
        setPage(1);
    };


    return (
        <div className="flex min-h-screen flex-col">
            <PopoverComponentCustom title="Presenze Aree Lavoro" />

            {profile?.customer_type === "company" && !isProfileComplete && profile?.company?.email === profile?.the_real_user_email && (
                <div className="w-full">
                    <div className="mb-6 bg-orange-100 p-4 text-orange-600">
                        <p className="text-center text-sm font-semibold">
                            Il profilo non è completo.<br />
                            Compila i campi mancanti{" "}
                            <Link href="/profilo/fatturazione" className="text-indigo-600 underline hover:text-indigo-800">cliccando qui</Link>
                        </p>
                    </div>
                </div>
            )}

            <main className="mt-10 flex-grow pb-8">
                <div className="mx-auto w-full max-w-[1600px] px-4 md:px-6">

                    <WorkingAreaPresenceContextBar
                        workingAreas={workingAreaOptions}
                        selectedWorkingArea={selectedWorkingArea}
                        onWorkingAreaChange={handleWorkingAreaChange}
                        pageSize={pageSize}
                        onPageSizeChange={handlePageSizeChange}
                        totalCount={totalCount}
                    />

                    <div className="mt-5 flex items-center justify-between rounded-lg border border-indigo-100 bg-indigo-50/50 px-4 py-2.5">
                        <div className="flex items-center gap-2 text-indigo-800">
                            <ClockIcon className="h-6 w-6 shrink-0" />

                            <span>
                                Presenze aggiornate al <span className="font-semibold">{lastUpdated.toLocaleDateString("it-IT")}</span>{" "}
                                alle ore <span className="font-semibold">{lastUpdated.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
                            </span>
                        </div>

                        <button
                            type="button"
                            onClick={handleRefresh}
                            disabled={loading}
                            className="rounded-md border border-indigo-200 bg-indigo-700 px-3 py-1.5 text-xs font-semibold uppercase text-white transition hover:bg-indigo-200 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {loading ? <><CircularProgress size={14} className="mr-2" />Aggiornamento...</> : "Aggiorna"}
                        </button>
                    </div>

                    <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
                        <SummaryCard title="Ingressi oggi" value={totalCheckins} description="Timbrature di ingresso registrate nella giornata" icon={ArrowRightOnRectangleIcon} />
                        <SummaryCard title="Uscite oggi" value={totalCheckouts} description="Timbrature concluse con uscita registrata" icon={ArrowLeftOnRectangleIcon} />
                        <SummaryCard title="Presenti in area" value={presencesNow} description="Dipendenti attualmente presenti nelle aree" icon={UsersIcon} />
                    </div>

                    <div className="mt-5 overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
                        <div className="border-b border-gray-300 px-5 py-4">
                            <h2 className="text-sm font-semibold uppercase tracking-wide text-indigo-800">Presenze per area di lavoro</h2>
                            <p className="mt-1 text-xs text-gray-500">Situazione aggiornata delle entrate, uscite e presenze correnti</p>
                        </div>

                        {loading ? (
                            <div className="flex justify-center py-14">
                                <CircularProgress />
                            </div>
                        ) : error ? (
                            <div className="p-5">
                                <Alert severity="error">{error}</Alert>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse text-sm">
                                    <thead className="bg-gray-50">
                                        <tr className="border-b border-gray-300">
                                            <th className="w-12 px-4 py-3" />
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Area di lavoro</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Ingressi</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Uscite</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Presenti</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {workingAreas.length > 0 ? (
                                            workingAreas.map((area) => (
                                                <WorkingAreaPresenceRow key={area.id} token={token} area={area} refreshKey={detailsRefreshKey} />
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={5} className="px-5 py-12 text-center">
                                                    <MapPinIcon className="mx-auto h-9 w-9 text-gray-300" />
                                                    <p className="mt-3 text-sm font-medium text-gray-600">Nessuna presenza disponibile</p>
                                                    <p className="mt-1 text-xs text-gray-400">Non risultano timbrature associate alle aree selezionate</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <PrettyPagination
                        page={page}
                        totalPages={totalPages}
                        totalCount={totalCount}
                        onChange={setPage}
                        labelSingolare="area"
                        labelPlurale="aree"
                        className="rounded-b-xl border-x border-b border-gray-200 bg-white px-4 py-4"
                    />
                </div>
            </main>

            <Footer />
        </div>
    );
}